/**
 * TUVI - Compras registradas (pestaña "Compras" del Carrito de Compras General)
 *
 * Cada compra arranca "En gestión". Aquí se ajustan proyectos, capítulos, cantidades y
 * valores (si algo no llegó o se compró en otra parte), se marca lo que llegó y, al
 * recibirlo, el costo pasa al presupuesto del proyecto (Control de Presupuesto y
 * Vista Financiera). También la factura, los adjuntos y la anulación.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import client from '../../services/graphqlClient';
import { useNotification } from '../../context/NotificationContext';
import { subirArchivoCompra, urlArchivoCompra, adjuntarAFactura, money } from './comprasArchivos';

const ESTADOS = {
  en_gestion: { txt: 'En gestión', cls: 'gestion' },
  parcial: { txt: 'Recibida parcial', cls: 'parcial' },
  recibida: { txt: 'Recibida', cls: 'recibida' },
  anulada: { txt: 'Anulada', cls: 'anulada' },
};
const FILTROS = [
  { k: 'activas', txt: 'En gestión', test: c => ['en_gestion', 'parcial'].includes(c.estado) },
  { k: 'recibida', txt: 'Recibidas', test: c => c.estado === 'recibida' },
  { k: 'anulada', txt: 'Anuladas', test: c => c.estado === 'anulada' },
  { k: 'todas', txt: 'Todas', test: () => true },
];
const QUERY = `query { comprasCarrito {
  id proveedor_id proveedor_nombre numero_factura fecha_compra fecha_vencimiento porcentaje_iva subtotal iva total
  crear_factura factura_id factura_estado factura_pagado estado observaciones usuario_nombre created_at recibida_at anulada_at anulada_por
  items { id cotizacion_id source linea_id consecutivo proyecto_id proyecto_nombre apartamento codigo descripcion unidad
    cantidad valor_unitario valor_total capitulo_id capitulo_nombre capitulos { id nombre } recibido recibido_at recibido_por }
  adjuntos { id item_id nombre archivo_key tamano usuario_nombre created_at } } }`;

const num = (v) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 2 });
const fecha = (s) => (s ? new Date(s.length === 10 ? s + 'T12:00:00' : s).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const etiquetaProyecto = (p) => `${p.proyecto_nombre || ''}${p.apartamento ? ' ' + p.apartamento : ''} — ${p.nombre_cliente || ''} (#${p.id})`;

export default function ComprasRegistradas({ onCambio, compraResaltada }) {
  const { success, error, warning } = useNotification();
  const [compras, setCompras] = useState(null);
  const [proyectos, setProyectos] = useState([]);
  const [filtro, setFiltro] = useState('activas');
  const [buscar, setBuscar] = useState('');
  const [abiertos, setAbiertos] = useState({});
  const [borrador, setBorrador] = useState({});   // compraId → { cab: {...}, items: { itemId: {...} } }
  const [selRecibir, setSelRecibir] = useState({}); // compraId → Set(itemId)
  const [ocupado, setOcupado] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const d = await client.query(QUERY);
      setCompras(d.comprasCarrito || []);
    } catch (e) {
      console.error('[Compras carrito]', e);
      setCompras([]);
      error(`Error cargando las compras: ${e.message || e}`);
    }
  }, [error]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    client.query('{ proyectos { id proyecto_nombre nombre_cliente apartamento } }')
      .then(d => setProyectos(d?.proyectos || [])).catch(() => setProyectos([]));
  }, []);
  useEffect(() => { if (compraResaltada) setAbiertos(p => ({ ...p, [compraResaltada]: true })); }, [compraResaltada]);

  const proyPorEtiqueta = useMemo(() => new Map(proyectos.map(p => [etiquetaProyecto(p), p])), [proyectos]);
  const proyPorId = useMemo(() => new Map(proyectos.map(p => [p.id, p])), [proyectos]);

  const filtradas = useMemo(() => {
    const f = FILTROS.find(x => x.k === filtro);
    const q = buscar.trim().toLowerCase();
    return (compras || []).filter(c => f.test(c) && (!q || [
      `#${c.id}`, c.proveedor_nombre, c.numero_factura, c.observaciones, c.usuario_nombre,
      ...c.items.flatMap(i => [i.codigo, i.descripcion, i.proyecto_nombre, i.apartamento, `COT-${i.consecutivo}`]),
    ].join(' ').toLowerCase().includes(q)));
  }, [compras, filtro, buscar]);

  // ===== Borrador de edición =====
  const cab = (c) => ({
    numero_factura: c.numero_factura || '', fecha_compra: c.fecha_compra || '', fecha_vencimiento: c.fecha_vencimiento || '',
    porcentaje_iva: String(c.porcentaje_iva ?? 0), observaciones: c.observaciones || '',
    ...(borrador[c.id]?.cab || {}),
  });
  const itemB = (c, i) => ({
    cantidad: String(i.cantidad), valor_unitario: String(i.valor_unitario), capitulo_id: i.capitulo_id || '',
    proyTexto: i.proyecto_id ? (proyPorId.get(i.proyecto_id) ? etiquetaProyecto(proyPorId.get(i.proyecto_id)) : `${i.proyecto_nombre || ''} ${i.apartamento || ''}`.trim()) : '',
    proyecto_id: i.proyecto_id, eliminar: false,
    ...(borrador[c.id]?.items?.[i.id] || {}),
  });
  const setCab = (c, cambios) => setBorrador(p => ({ ...p, [c.id]: { ...(p[c.id] || {}), cab: { ...(p[c.id]?.cab || {}), ...cambios } } }));
  const setItem = (c, i, cambios) => setBorrador(p => ({
    ...p, [c.id]: { ...(p[c.id] || {}), items: { ...(p[c.id]?.items || {}), [i.id]: { ...(p[c.id]?.items?.[i.id] || {}), ...cambios } } },
  }));
  const sucio = (c) => !!borrador[c.id] && (Object.keys(borrador[c.id].cab || {}).length > 0 || Object.keys(borrador[c.id].items || {}).length > 0);
  const descartar = (c) => setBorrador(p => { const n = { ...p }; delete n[c.id]; return n; });

  const accion = async (c, fn) => {
    setOcupado(c.id);
    try {
      const r = await fn();
      if (r?.success) { success(r.message); await cargar(); onCambio?.(); return true; }
      if (r) error(r.message);
      return false;
    } catch (e) { error(e.message || 'No se pudo guardar'); return false; }
    finally { setOcupado(null); }
  };

  const guardar = (c) => {
    const b = borrador[c.id] || {};
    const items = [];
    for (const i of c.items) {
      const ib = b.items?.[i.id];
      if (!ib) continue;
      const v = itemB(c, i);
      if (ib.proyTexto !== undefined && ib.proyTexto.trim() && !v.proyecto_id) { warning(`Elige el proyecto de "${i.codigo}" de la lista`); return; }
      items.push({
        id: i.id, eliminar: !!v.eliminar,
        cantidad: Number(v.cantidad), valor_unitario: Number(v.valor_unitario),
        ...(v.proyecto_id !== i.proyecto_id ? { proyecto_id: v.proyecto_id || null } : {}),
        ...(v.capitulo_id && Number(v.capitulo_id) !== i.capitulo_id && v.proyecto_id === i.proyecto_id ? { capitulo_id: Number(v.capitulo_id) } : {}),
      });
    }
    const input = { ...(b.cab ? { ...b.cab, porcentaje_iva: b.cab.porcentaje_iva !== undefined ? Number(b.cab.porcentaje_iva) || 0 : undefined } : {}), items };
    return accion(c, async () => {
      const r = await client.mutate('mutation($id: Int!, $input: EditarCompraCarritoInput!) { editarCompraCarrito(id: $id, input: $input) { success message } }', { id: c.id, input });
      if (r.editarCompraCarrito.success) descartar(c);
      return r.editarCompraCarrito;
    });
  };

  const recibir = (c, ids, recibido) => {
    if (!ids.length) return warning('Selecciona los ítems');
    if (recibido && sucio(c)) return warning('Guarda los cambios antes de marcar lo que llegó');
    return accion(c, async () => {
      const r = await client.mutate('mutation($id: Int!, $ids: [Int!]!, $r: Boolean!) { recibirItemsCompraCarrito(id: $id, item_ids: $ids, recibido: $r) { success message } }', { id: c.id, ids, r: recibido });
      if (r.recibirItemsCompraCarrito.success) setSelRecibir(p => ({ ...p, [c.id]: new Set() }));
      return r.recibirItemsCompraCarrito;
    });
  };

  const crearFactura = (c) => {
    if (sucio(c)) return warning('Guarda los cambios primero');
    return accion(c, async () => {
      const r = await client.mutate('mutation($id: Int!) { crearFacturaCompraCarrito(id: $id) { success message } }', { id: c.id });
      return r.crearFacturaCompraCarrito;
    });
  };

  const anular = (c) => {
    if (!window.confirm(`¿Anular la compra #${c.id}? Lo comprado vuelve a quedar pendiente en el carrito${c.factura_id ? ' y se elimina su factura de Gestión de Pagos' : ''}.`)) return;
    return accion(c, async () => {
      const r = await client.mutate('mutation($id: Int!) { anularCompraCarrito(id: $id) { success message } }', { id: c.id });
      return r.anularCompraCarrito;
    });
  };

  const agregarAdjuntos = async (c, files, itemId) => {
    if (!files.length) return;
    await accion(c, async () => {
      const adjuntos = [];
      for (const f of files) adjuntos.push({ ...(await subirArchivoCompra(f)), item_id: itemId || null });
      const r = await client.mutate('mutation($id: Int!, $a: [AdjuntoCompraInput!]!) { agregarAdjuntosCompraCarrito(id: $id, adjuntos: $a) { success message } }', { id: c.id, a: adjuntos });
      if (r.agregarAdjuntosCompraCarrito.success && c.factura_id && !c.adjuntos.length) await adjuntarAFactura(c.factura_id, files[0]).catch(() => {});
      return r.agregarAdjuntosCompraCarrito;
    });
  };

  const eliminarAdjunto = (c, a) => {
    if (!window.confirm(`¿Eliminar el adjunto "${a.nombre || 'archivo'}"?`)) return;
    return accion(c, async () => {
      const r = await client.mutate('mutation($id: Int!) { eliminarAdjuntoCompraCarrito(id: $id) { success message } }', { id: a.id });
      return r.eliminarAdjuntoCompraCarrito;
    });
  };

  // ===== Render =====
  const renderCompra = (c) => {
    const est = ESTADOS[c.estado] || { txt: c.estado, cls: '' };
    const abierto = !!abiertos[c.id];
    const editable = c.estado !== 'anulada';
    const h = cab(c);
    const recibidos = c.items.filter(i => i.recibido).length;
    const sel = selRecibir[c.id] || new Set();
    const toggleSel = (id) => setSelRecibir(p => { const s = new Set(p[c.id] || []); s.has(id) ? s.delete(id) : s.add(id); return { ...p, [c.id]: s }; });
    const proyectosCompra = [...new Set(c.items.map(i => `${i.proyecto_nombre || 'Sin proyecto'} ${i.apartamento || ''}`.trim()))];
    // Subtotal del borrador (para ver el efecto antes de guardar)
    const subB = c.items.reduce((s, i) => { const v = itemB(c, i); return v.eliminar ? s : s + (Number(v.cantidad) || 0) * (Number(v.valor_unitario) || 0); }, 0);
    const totB = subB * (1 + (Number(h.porcentaje_iva) || 0) / 100);
    const pendientesSel = c.items.filter(i => sel.has(i.id) && !i.recibido).map(i => i.id);
    const recibidosSel = c.items.filter(i => sel.has(i.id) && i.recibido).map(i => i.id);

    return (
      <div key={c.id} className={`cmp-compra ${est.cls}`}>
        <div className="cmp-compra-header" onClick={() => setAbiertos(p => ({ ...p, [c.id]: !abierto }))}>
          <span className="ccg-flecha">{abierto ? '▾' : '▸'}</span>
          <span className="cmp-compra-num">#{c.id}</span>
          <span className="cmp-compra-prov">{c.proveedor_nombre}</span>
          <span className="cmp-compra-meta">
            {c.numero_factura ? `Fact. ${c.numero_factura}` : 'Sin N° factura'} · {fecha(c.fecha_compra)} · {c.items.length} ítem{c.items.length === 1 ? '' : 's'}
            {proyectosCompra.length ? ` · ${proyectosCompra.length > 2 ? `${proyectosCompra.length} proyectos` : proyectosCompra.join(', ')}` : ''}
          </span>
          <span className="cmp-compra-total">{money(c.total)}</span>
          {c.factura_id
            ? <span className={`cmp-tag-factura ${c.factura_estado === 'pagada' ? 'pagada' : ''}`}>Factura {c.factura_estado === 'pagada' ? 'pagada' : Number(c.factura_pagado) > 0 ? `abonada ${money(c.factura_pagado)}` : 'pendiente'}</span>
            : c.estado !== 'anulada' && <span className="cmp-tag-factura sin">Sin factura</span>}
          <span className={`cmp-estado ${est.cls}`}>{est.txt}{c.estado === 'parcial' ? ` ${recibidos}/${c.items.length}` : ''}</span>
        </div>

        {abierto && (
          <div className="cmp-compra-body">
            {c.estado === 'anulada' && <p className="cmp-aviso anulada">Anulada por {c.anulada_por} el {fecha(c.anulada_at)}.</p>}

            {/* Cabecera editable */}
            <div className="cmp-form-grid compacto">
              <label className="cmp-campo">N° factura
                <input type="text" value={h.numero_factura} disabled={!editable} onChange={e => setCab(c, { numero_factura: e.target.value })} />
              </label>
              <label className="cmp-campo">Fecha compra
                <input type="date" value={h.fecha_compra} disabled={!editable} onChange={e => setCab(c, { fecha_compra: e.target.value })} />
              </label>
              <label className="cmp-campo">Vencimiento
                <input type="date" value={h.fecha_vencimiento} disabled={!editable} onChange={e => setCab(c, { fecha_vencimiento: e.target.value })} />
              </label>
              <label className="cmp-campo">IVA %
                <input type="number" min="0" step="any" value={h.porcentaje_iva} disabled={!editable} onChange={e => setCab(c, { porcentaje_iva: e.target.value })} />
              </label>
              <label className="cmp-campo cmp-campo-ancho">Observaciones
                <input type="text" value={h.observaciones} disabled={!editable} onChange={e => setCab(c, { observaciones: e.target.value })} />
              </label>
            </div>

            {/* Ítems */}
            <table className="ccg-table cmp-tabla-items">
              <thead>
                <tr>
                  <th></th><th>Proyecto destino</th><th>Capítulo</th><th>Código</th><th>Descripción</th>
                  <th>Cant.</th><th>Valor unit.</th><th>Total</th><th>Estado</th><th></th>
                </tr>
              </thead>
              <tbody>
                {c.items.map(i => {
                  const v = itemB(c, i);
                  const bloqueado = !editable || i.recibido;
                  const proyCambiado = v.proyecto_id !== i.proyecto_id;
                  return (
                    <tr key={i.id} className={`${i.recibido ? 'ccg-hecho' : ''} ${v.eliminar ? 'cmp-eliminado' : ''}`}>
                      <td>{editable && <input type="checkbox" className="ccg-check" checked={sel.has(i.id)} onChange={() => toggleSel(i.id)} />}</td>
                      <td className="cmp-td-proy">
                        {bloqueado ? (
                          <span><strong>{i.proyecto_nombre || '—'}</strong> {i.apartamento}{i.consecutivo ? <div className="ccg-cliente">COT-{i.consecutivo}</div> : null}</span>
                        ) : (
                          <>
                            <input type="text" list={`cmp-proys-${c.id}`} value={v.proyTexto} placeholder="Proyecto (escribe o pega)"
                              className={v.proyTexto && !v.proyecto_id ? 'cmp-invalido' : ''}
                              onChange={e => { const p = proyPorEtiqueta.get(e.target.value); setItem(c, i, { proyTexto: e.target.value, proyecto_id: p ? p.id : null }); }} />
                            {i.consecutivo && !proyCambiado ? <div className="ccg-cliente">COT-{i.consecutivo}</div> : null}
                          </>
                        )}
                      </td>
                      <td>
                        {bloqueado || proyCambiado || !i.capitulos.length ? (
                          <span className={i.capitulo_nombre ? '' : 'ccg-sin'}>{proyCambiado ? 'Se asigna al guardar' : (i.capitulo_nombre || (i.proyecto_id ? 'Se asigna al recibir' : '—'))}</span>
                        ) : (
                          <select value={v.capitulo_id} onChange={e => setItem(c, i, { capitulo_id: e.target.value })}>
                            {!v.capitulo_id && <option value="">Elegir...</option>}
                            {i.capitulos.map(k => <option key={k.id} value={k.id}>{k.nombre}</option>)}
                          </select>
                        )}
                      </td>
                      <td className="ccg-codigo">{i.codigo}</td>
                      <td className="cmp-td-desc">
                        {i.descripcion}
                        {c.adjuntos.filter(a => a.item_id === i.id).map(a => (
                          <a key={a.id} className="cmp-adj-link" href={urlArchivoCompra(a.archivo_key)} target="_blank" rel="noreferrer">📎 {a.nombre}</a>
                        ))}
                      </td>
                      <td>{bloqueado ? <span className="ccg-num">{num(i.cantidad)} {i.unidad}</span>
                        : <input type="number" min="0" step="any" className="cmp-cant" value={v.cantidad} onChange={e => setItem(c, i, { cantidad: e.target.value })} />}</td>
                      <td>{bloqueado ? <span className="ccg-num">{money(i.valor_unitario)}</span>
                        : <input type="number" min="0" className="cmp-vu-input" value={v.valor_unitario} onChange={e => setItem(c, i, { valor_unitario: e.target.value })} />}</td>
                      <td className="ccg-num">{money((Number(v.cantidad) || 0) * (Number(v.valor_unitario) || 0))}</td>
                      <td className="ccg-nowrap">
                        {i.recibido
                          ? <span className="ccg-tag-ok" title={`Por ${i.recibido_por || ''}`}>Llegó {fecha(i.recibido_at)}</span>
                          : c.estado === 'anulada' ? <span className="ccg-tag-anulada">Anulado</span> : <span className="cmp-tag-gestion">En gestión</span>}
                      </td>
                      <td>
                        {!bloqueado && (
                          <button type="button" className="cmp-btn-x" title={v.eliminar ? 'No quitar' : 'Quitar de la compra (vuelve a quedar pendiente)'}
                            onClick={() => setItem(c, i, { eliminar: !v.eliminar })}>{v.eliminar ? '↺' : '×'}</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <datalist id={`cmp-proys-${c.id}`}>{proyectos.map(p => <option key={p.id} value={etiquetaProyecto(p)} />)}</datalist>

            <div className="cmp-compra-totales">
              <span>Subtotal <strong>{money(sucio(c) ? subB : c.subtotal)}</strong></span>
              <span>IVA ({num(h.porcentaje_iva)}%) <strong>{money(sucio(c) ? totB - subB : c.iva)}</strong></span>
              <span className="cmp-total">Total <strong>{money(sucio(c) ? totB : c.total)}</strong></span>
              <span className="cmp-quien">Registrada por {c.usuario_nombre} · {fecha(c.created_at)}</span>
            </div>

            {/* Adjuntos */}
            <div className="cmp-adjuntos">
              <strong>Adjuntos</strong>
              {c.adjuntos.length === 0 && <span className="ccg-sin">Sin adjuntos</span>}
              {c.adjuntos.map(a => {
                const it = a.item_id ? c.items.find(i => i.id === a.item_id) : null;
                return (
                  <span key={a.id} className="cmp-archivo-chip">
                    <a href={urlArchivoCompra(a.archivo_key)} target="_blank" rel="noreferrer">{a.nombre || 'archivo'}</a>
                    {it ? <em> · {it.codigo}</em> : <em> · general</em>}
                    <a href={urlArchivoCompra(a.archivo_key, true)} title="Descargar">⬇</a>
                    {editable && <button type="button" onClick={() => eliminarAdjunto(c, a)} title="Eliminar">×</button>}
                  </span>
                );
              })}
              {editable && (
                <label className="cmp-btn-adjuntar">
                  📎 Agregar{sel.size === 1 ? ' al ítem seleccionado' : ''}
                  <input type="file" multiple onChange={e => { const fs = Array.from(e.target.files || []); e.target.value = ''; agregarAdjuntos(c, fs, sel.size === 1 ? [...sel][0] : null); }} />
                </label>
              )}
            </div>

            {/* Acciones */}
            {editable && (
              <div className="cmp-compra-acciones">
                {sucio(c) && (
                  <>
                    <button className="cmp-btn-prim" disabled={ocupado === c.id} onClick={() => guardar(c)}>Guardar cambios</button>
                    <button className="ccg-btn-sec" disabled={ocupado === c.id} onClick={() => descartar(c)}>Descartar</button>
                  </>
                )}
                <button className="cmp-btn-ok" disabled={ocupado === c.id || !pendientesSel.length} onClick={() => recibir(c, pendientesSel, true)}
                  title="Lo que llegó pasa al presupuesto del proyecto">
                  ✓ Llegó {pendientesSel.length ? `(${pendientesSel.length})` : ''}
                </button>
                {!sel.size && c.items.some(i => !i.recibido) && (
                  <button className="ccg-btn-sec" disabled={ocupado === c.id} onClick={() => setSelRecibir(p => ({ ...p, [c.id]: new Set(c.items.filter(i => !i.recibido).map(i => i.id)) }))}>
                    Seleccionar todo lo pendiente
                  </button>
                )}
                {recibidosSel.length > 0 && (
                  <button className="ccg-btn-sec" disabled={ocupado === c.id} onClick={() => recibir(c, recibidosSel, false)}>Desmarcar llegada ({recibidosSel.length})</button>
                )}
                {!c.factura_id && <button className="ccg-btn-sec" disabled={ocupado === c.id} onClick={() => crearFactura(c)}>Crear factura</button>}
                <span className="ccg-spacer" />
                {recibidos === 0 && <button className="cmp-btn-del" disabled={ocupado === c.id} onClick={() => anular(c)}>Anular compra</button>}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const conteo = (k) => (compras || []).filter(FILTROS.find(x => x.k === k).test).length;

  return (
    <div className="cmp-compras">
      <div className="ccg-filtros">
        <input type="text" className="ccg-buscar" placeholder="Buscar compra, proveedor, factura, proyecto, código..." value={buscar} onChange={e => setBuscar(e.target.value)} />
        <div className="ccg-vista">
          {FILTROS.map(f => (
            <button key={f.k} className={filtro === f.k ? 'active' : ''} onClick={() => setFiltro(f.k)}>{f.txt} ({conteo(f.k)})</button>
          ))}
        </div>
        <span className="ccg-spacer" />
        <button className="ccg-btn-sec" onClick={cargar}>Actualizar</button>
      </div>
      {compras === null ? <p className="ccg-vacio">Cargando...</p> : filtradas.length === 0 ? (
        <p className="ccg-vacio">{filtro === 'activas' ? 'No hay compras en gestión. Usa "Hacer compra" en un proveedor de la pestaña "Por proveedor".' : 'No hay compras con estos filtros.'}</p>
      ) : filtradas.map(renderCompra)}
    </div>
  );
}
